<template>
  <Modal
    :show="showModal"
    title="Add New Contact"
    size="lg"
    @close="closeModal"
    class="add-contact-modal"
  >
    <Form ref="contactFormRef" class="form" @submit="handleFormSubmit">
      <div v-if="!enrichmentResult" class="linkedin-quick-add-section">
        <Button type="button" variant="primary" size="sm" rounded disabled class="quick-add-button">
          <Icon icon="mdi:auto-fix" class="button-icon-left" />
          Quick Add with LinkedIn
          <Icon icon="mdi:linkedin" class="button-icon-right" />
        </Button>
        <div class="linkedin-input-wrapper">
          <Input
            v-model="formData.linkedInUrl"
            placeholder="https://www.linkedin.com/in/username"
            leading-icon="mdi:linkedin"
            :disabled="isEnrichmentLoading"
            size="md"
            class="linkedin-url-input"
          />
          <Button
            v-tooltip="{
              content: USAGE_BLOCKED_TOOLTIP,
              disabled: !isUsageBlocked || isEnrichmentLoading || !formData.linkedInUrl,
            }"
            type="button"
            variant="primary"
            size="md"
            icon="mdi:auto-fix"
            :loading="isEnrichmentLoading"
            :disabled="isEnrichmentLoading || !formData.linkedInUrl || isUsageBlocked"
            @click="handleRequestEnrichment"
            class="enrich-button"
          >
            Enrich
          </Button>
        </div>
        <div class="enrich-options">
          <div
            class="enrich-checkbox-wrapper"
            @click.stop="formData.enrichEmail = !formData.enrichEmail"
          >
            <Checkbox
              v-model="formData.enrichEmail"
              size="md"
              class="enrich-checkbox"
              @click.stop
            />
            <Icon icon="mdi:email-outline" class="checkbox-icon" />
            <span class="checkbox-label-text">Enrich Email</span>
          </div>
          <div
            class="enrich-checkbox-wrapper"
            @click.stop="formData.enrichPhoneNumber = !formData.enrichPhoneNumber"
          >
            <Checkbox
              v-model="formData.enrichPhoneNumber"
              size="md"
              class="enrich-checkbox"
              @click.stop
            />
            <Icon icon="mdi:phone-outline" class="checkbox-icon" />
            <span class="checkbox-label-text">Enrich Phone</span>
          </div>
        </div>
        <div class="enrichment-info-notice">
          <Icon icon="mdi:information-outline" class="notice-icon" />
          <span
            >Enrichment uses credits and is processed in the background. You'll receive a
            notification when complete.</span
          >
        </div>
      </div>

      <div v-else class="enrichment-sent-state">
        <Icon icon="mdi:check-circle-outline" class="enrichment-sent-icon" />
        <div class="enrichment-sent-info">
          <span class="enrichment-sent-title">Enrichment request sent</span>
          <span class="enrichment-sent-sub"
            >Processing in the background — you'll be notified when complete.</span
          >
        </div>
      </div>

      <div class="section-divider">
        <span class="divider-label">CONTACT DETAILS</span>
        <div class="divider-line"></div>
      </div>

      <div v-if="!enrichmentResult" class="contact-details-form">
        <div class="form-row">
          <div class="form-column">
            <Field
              id="fname"
              v-model="formData.fname"
              :rules="enrichmentResult ? {} : { required: ['First Name'] }"
              name="fname"
              v-slot="{ field, errorMessage }"
            >
              <Input
                :model-value="field.value"
                @update:model-value="field.onChange($event)"
                @blur="field.onBlur"
                label="First Name"
                placeholder="Enter first name"
                required
                :error="errorMessage"
                leading-icon="mdi:account-outline"
                :disabled="isEnrichmentLoading"
                size="md"
              />
            </Field>

            <Field
              id="jobTitle"
              v-model="formData.jobTitle"
              :rules="{}"
              name="jobTitle"
              v-slot="{ field, errorMessage }"
            >
              <Input
                :model-value="field.value"
                @update:model-value="field.onChange($event)"
                @blur="field.onBlur"
                label="Job Title"
                placeholder="Sales Manager"
                :error="errorMessage"
                :disabled="isEnrichmentLoading"
                size="md"
              />
            </Field>
            <ErrorMessage name="jobTitle" class="form-error" />

            <Field
              id="website"
              v-model="formData.website"
              :rules="{}"
              type="text"
              name="website"
              v-slot="{ field, errorMessage }"
            >
              <Input
                :model-value="field.value"
                @update:model-value="field.onChange($event)"
                @blur="field.onBlur"
                label="Website"
                placeholder="https://example.com"
                leading-icon="mdi:web"
                :error="errorMessage"
                :disabled="isEnrichmentLoading"
                size="md"
              />
            </Field>
            <ErrorMessage name="website" class="form-error" />
          </div>

          <div class="form-column">
            <Field
              id="lname"
              v-model="formData.lname"
              :rules="enrichmentResult ? {} : { required: ['Last Name'] }"
              name="lname"
              v-slot="{ field, errorMessage }"
            >
              <Input
                :model-value="field.value"
                @update:model-value="field.onChange($event)"
                @blur="field.onBlur"
                label="Last Name"
                placeholder="Enter last name"
                required
                :error="errorMessage"
                :disabled="isEnrichmentLoading"
                size="md"
              />
            </Field>

            <div class="global-search-container">
              <Field
                id="company"
                type="text"
                name="company"
                v-model="companySearchTerm"
                @input="handleCompanySearch"
                @focus="showSearchDropdown = true"
                @blur="handleHideSearchDropdown"
                :disabled="isEnrichmentLoading"
                v-slot="{ field, errorMessage }"
              >
                <Input
                  :model-value="field.value"
                  @update:model-value="field.onChange($event)"
                  @blur="field.onBlur"
                  label="Company"
                  placeholder="Acme Inc."
                  leading-icon="mdi:office-building-outline"
                  :error="errorMessage"
                  :disabled="isEnrichmentLoading"
                  size="md"
                />
              </Field>
              <ErrorMessage name="company" class="form-error" />
              <div
                v-if="
                  showSearchDropdown && companySearchTerm && companiesData && companiesData.length
                "
                class="global-search-dropdown"
              >
                <div
                  v-for="(company, idx) in companiesData"
                  :key="idx"
                  class="global-search-item"
                  @click="handleSelectCompany(company)"
                >
                  <h6 class="company-name">{{ capitalize(company.name) }}</h6>
                </div>
              </div>
            </div>

            <Field
              id="industry"
              v-model="formData.industry"
              type="text"
              name="industry"
              v-slot="{ field, errorMessage }"
            >
              <Select
                :model-value="field.value"
                @update:model-value="field.onChange($event)"
                @blur="field.onBlur"
                label="Industry"
                :options="industryOptions"
                placeholder="Technology"
                :error="errorMessage"
                size="md"
              />
            </Field>
            <ErrorMessage name="industry" class="form-error" />
          </div>
        </div>

        <div class="dynamic-section full-width-section">
          <div class="section-header">
            <label class="section-label">Phone Numbers</label>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              icon="ph:plus"
              icon-position="left"
              @click="addAdditionalPhoneNumber"
              :disabled="isEnrichmentLoading"
              class="add-field-btn-header"
            >
              Add new Phone
            </Button>
          </div>
          <template
            v-for="(phoneNumber, index) in formData.labeledAdditionalPhoneNumbers"
            :key="index"
          >
            <div class="phone-number-row" :class="{ 'additional-field': index > 0 }">
              <div v-if="index > 0" class="additional-field-header">
                <button
                  type="button"
                  class="remove-additional-btn"
                  @click="removeAdditionalPhoneNumber(index)"
                  :disabled="isEnrichmentLoading"
                  title="Remove this phone number"
                >
                  <Icon icon="mdi:close" />
                </button>
              </div>
              <div class="phone-fields-wrapper">
                <div class="phone-type-select">
                  <Select
                    v-model="formData.labeledAdditionalPhoneNumbers[index].label"
                    :options="phoneLabelOptions"
                    placeholder="Select type"
                    size="md"
                  />
                </div>
                <div class="phone-input-wrapper">
                  <PhoneInput
                    v-model="formData.labeledAdditionalPhoneNumbers[index].number"
                    defaultCountry="US"
                    :disabled="isEnrichmentLoading"
                    size="md"
                  />
                </div>
              </div>
            </div>
          </template>
        </div>

        <div class="dynamic-section full-width-section">
          <div class="section-header">
            <label class="section-label">Email Addresses</label>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              icon="ph:plus"
              icon-position="left"
              @click="addAdditionalEmail"
              :disabled="isEnrichmentLoading"
              class="add-field-btn-header"
            >
              Add new Email
            </Button>
          </div>
          <div class="main-email-field">
            <Field
              id="email"
              v-model="formData.email"
              type="email"
              name="email"
              v-slot="{ field, errorMessage }"
            >
              <Input
                :model-value="field.value"
                @update:model-value="field.onChange($event)"
                @blur="field.onBlur"
                type="email"
                placeholder="Enter an email address"
                :error="errorMessage"
                :disabled="isEnrichmentLoading"
                size="md"
              />
            </Field>
          </div>
          <template v-for="(email, index) in formData.additionalEmails" :key="index">
            <div class="email-row additional-field">
              <div class="additional-field-header">
                <button
                  type="button"
                  class="remove-additional-btn"
                  @click="removeAdditionalEmail(index)"
                  :disabled="isEnrichmentLoading"
                  title="Remove this email"
                >
                  <Icon icon="mdi:close" />
                </button>
              </div>
              <Field
                :id="`email${index}`"
                v-model="formData.additionalEmails[index]"
                :rules="{ falseyEmail: ['Email'] }"
                type="email"
                :name="`email${index}`"
                v-slot="{ field, errorMessage }"
              >
                <Input
                  :model-value="field.value"
                  @update:model-value="field.onChange($event)"
                  @blur="field.onBlur"
                  type="email"
                  placeholder="Enter an email address"
                  :error="errorMessage"
                  :disabled="isEnrichmentLoading"
                  size="md"
                />
              </Field>
            </div>
          </template>
        </div>

        <div v-if="customFieldDefs?.length" class="custom-fields-section full-width-section">
          <label class="section-label">Custom Fields</label>
          <div class="custom-fields-list">
            <div v-for="def in customFieldDefs" :key="def.id" class="custom-field-entry">
              <!-- Text / URL / Number -->
              <Input
                v-if="['text', 'url', 'number'].includes(def.fieldType)"
                :model-value="getCustomFieldValue(def.id)"
                @update:model-value="setCustomFieldValue(def.id, $event)"
                :label="def.label + (def.isRequired ? ' *' : '')"
                :placeholder="`Enter ${def.label.toLowerCase()}`"
                :type="def.fieldType === 'number' ? 'number' : 'text'"
                :disabled="isEnrichmentLoading"
                size="md"
              />
              <!-- Date -->
              <div v-else-if="def.fieldType === 'date'" class="custom-field-date">
                <label class="section-label">{{ def.label }}{{ def.isRequired ? " *" : "" }}</label>
                <Input
                  :model-value="getCustomFieldValue(def.id)"
                  @update:model-value="setCustomFieldValue(def.id, $event)"
                  type="date"
                  :disabled="isEnrichmentLoading"
                  size="md"
                />
              </div>
              <!-- Select -->
              <Select
                v-else-if="def.fieldType === 'select'"
                :model-value="getCustomFieldValue(def.id)"
                @update:model-value="setCustomFieldValue(def.id, $event)"
                :label="def.label + (def.isRequired ? ' *' : '')"
                :options="def.options.map((o) => ({ label: o, value: o }))"
                size="md"
              />
              <!-- Multi-select -->
              <MultiSelect
                v-else-if="def.fieldType === 'multi_select'"
                :model-value="getCustomFieldValue(def.id) || []"
                @update:model-value="setCustomFieldValue(def.id, $event)"
                :label="def.label + (def.isRequired ? ' *' : '')"
                :options="def.options.map((o) => ({ label: o, value: o }))"
                size="md"
              />
              <!-- Checkbox -->
              <div v-else-if="def.fieldType === 'checkbox'" class="custom-field-checkbox">
                <Checkbox
                  :model-value="!!getCustomFieldValue(def.id)"
                  @update:model-value="setCustomFieldValue(def.id, $event)"
                  :label="def.label"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="bucket-info-notice">
        <Icon icon="mdi:information-outline" class="notice-icon" />
        <span
          >Save the contact to a bucket to track them in your pipeline and assign a follow-up
          task.</span
        >
      </div>

      <div class="contact-details-form">
        <div class="form-row">
          <div class="form-column">
            <Field
              id="taskType"
              v-model="formData.taskType"
              :rules="{ required: ['Task Type'] }"
              name="taskType"
              v-slot="{ field, errorMessage }"
            >
              <Select
                :model-value="field.value"
                @update:model-value="field.onChange($event)"
                @blur="field.onBlur"
                label="Task Type"
                :options="taskTypeOptions"
                placeholder="Select task type"
                required
                :error="errorMessage"
                size="md"
              />
            </Field>
          </div>
          <div class="form-column">
            <Field
              id="bucket"
              v-model="formData.bucketOptionId"
              :rules="{ required: ['Bucket'] }"
              name="bucket"
              v-slot="{ field, errorMessage }"
            >
              <Select
                :model-value="field.value"
                @update:model-value="field.onChange($event)"
                @blur="field.onBlur"
                label="Bucket"
                :options="bucketSelectOptions"
                placeholder="Select bucket"
                required
                :error="errorMessage"
                size="md"
              />
            </Field>
          </div>
        </div>

        <div class="form-group">
          <Field
            id="followUp"
            v-model="formData.followUpDate"
            :rules="{ required: ['Follow up date'] }"
            name="followUp"
            v-slot="{ field, errorMessage }"
          >
            <DateSelector
              :model-value="field.value"
              @update:model-value="field.onChange($event)"
              @blur="field.onBlur"
              label="Follow up date"
              :disable-past="false"
              required
            />
            <div v-if="errorMessage" class="form-error" style="margin-top: 4px">
              {{ errorMessage }}
            </div>
          </Field>
        </div>
      </div>
    </Form>

    <template #footer>
      <Button
        type="button"
        variant="secondary"
        size="md"
        @click="closeModal"
        :disabled="isRegistering"
      >
        Cancel
      </Button>
      <Button
        type="button"
        variant="primary"
        size="md"
        :loading="isRegistering"
        :disabled="isRegistering"
        @click="handleSubmit"
      >
        Save Contact
      </Button>
    </template>
  </Modal>
</template>

<script setup>
import { ref, computed, onMounted, getCurrentInstance } from "vue";
import { useToast } from "@/composables/ui/useToast";
import { useStore } from "vuex";
import debounce from "lodash/debounce";
import {
  Button,
  Checkbox,
  DateSelector,
  Icon,
  Input,
  Modal,
  MultiSelect,
  PhoneInput,
  Select,
} from "@/components/ui";
import { useActiveCustomFields } from "@/composables/settings/useCustomFields";
import { TaskType } from "@/utils/enums";
import { getTaskTypeIcon } from "@/utils/iconMapping";
import { INDUSTRY_OPTIONS } from "@/utils/const";
import { ErrorMessage, Field, Form, defineRule } from "vee-validate";
import { useRegisterContact, useRequestEnrichment } from "@/composables/contacts";
import { useBucketOptions } from "@/composables/bucket/useBucketOptions";
import { useCompanies } from "@/composables/companies/useCompanies";
import { useBillingStatus, USAGE_BLOCKED_TOOLTIP } from "@/composables/billing/useBillingStatus";
import { useFormDraft } from "@/composables/shared";

defineRule("email", (value) => {
  const emailRegExp = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegExp.test(value) || "Email is not valid";
});

defineRule("falseyEmail", (value) => {
  if (!value) {
    return true;
  }
  const emailRegExp = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegExp.test(value) || "Email is not valid";
});

defineRule("required", (value, [fieldName = "Field"]) => {
  if (!value || value.length === 0) {
    return `${fieldName} is required`;
  }
  return true;
});

const props = defineProps({
  selectedPhoneNumber: {
    type: String,
    default: null,
  },
});

const emit = defineEmits(["closeAddContactModal", "refreshContacts"]);

const instance = getCurrentInstance();
const $toast = useToast();
const $filters = instance?.proxy?.$filters;

const store = useStore();
const account = computed(() => store.getters.account?.data);
const organizationId = computed(() => account.value?.organizationId);

const contactFormRef = ref(null);
const showModal = ref(true);

const formData = ref({
  fname: null,
  lname: null,
  email: null,
  mobileNumber: null,
  website: null,
  industry: null,
  jobTitle: null,
  linkedInUrl: null,
  taskType: null,
  bucketOptionId: null,
  followUpDate: null,
  additionalEmails: [],
  additionalPhoneNumbers: [""],
  labeledAdditionalPhoneNumbers: [
    {
      number: null,
      label: "Mobile",
    },
  ],
  company: null,
  enrichEmail: true,
  enrichPhoneNumber: true,
  enrichmentId: null,
  organizationId: null,
  customFields: [],
});

const enrichmentResult = ref(null);

const companySearchTerm = ref(null);
const showSearchDropdown = ref(false);

// Draft persistence — there's no contact id yet (this creates a new one), so drafts
// are scoped per-org instead of per-contact: only one "add contact" draft is realistically
// in flight at a time. `companySearchTerm` lives outside `formData` (vee-validate binds the
// company text field to it directly), so it's folded into a merged computed source — the
// plain `formData` ref alone would miss edits made only to the company field.
const draftSource = computed(() => ({
  fname: formData.value.fname,
  lname: formData.value.lname,
  email: formData.value.email,
  mobileNumber: formData.value.mobileNumber,
  website: formData.value.website,
  industry: formData.value.industry,
  jobTitle: formData.value.jobTitle,
  linkedInUrl: formData.value.linkedInUrl,
  taskType: formData.value.taskType,
  bucketOptionId: formData.value.bucketOptionId,
  followUpDate: formData.value.followUpDate,
  additionalEmails: formData.value.additionalEmails,
  additionalPhoneNumbers: formData.value.additionalPhoneNumbers,
  labeledAdditionalPhoneNumbers: formData.value.labeledAdditionalPhoneNumbers,
  company: formData.value.company,
  companySearchTerm: companySearchTerm.value,
}));

const { loadDraft: loadAddContactDraft, clearDraft: clearAddContactDraft } = useFormDraft(
  draftSource,
  {
    key: `add-contact:${organizationId.value ?? "unknown"}`,
    isEmpty: (v) =>
      !v.fname &&
      !v.lname &&
      !v.email &&
      !v.mobileNumber &&
      !v.website &&
      !v.jobTitle &&
      !v.linkedInUrl &&
      !v.company &&
      !v.companySearchTerm,
  }
);

const bucketOptionsParams = computed(() => {
  if (!organizationId.value) return null;
  return { organizationId: organizationId.value };
});

const { data: bucketOptionsData } = useBucketOptions(bucketOptionsParams);

const { data: customFieldDefs } = useActiveCustomFields("contact");

const getCustomFieldValue = (fieldId) => {
  const entry = formData.value.customFields?.find((f) => f.fieldId === fieldId);
  return entry?.value ?? null;
};

const setCustomFieldValue = (fieldId, value) => {
  const arr = formData.value.customFields || [];
  const idx = arr.findIndex((f) => f.fieldId === fieldId);
  if (idx >= 0) {
    arr[idx] = { fieldId, value };
  } else {
    arr.push({ fieldId, value });
  }
  formData.value.customFields = [...arr];
};

const companiesSearchParams = computed(() => {
  if (!organizationId.value || !companySearchTerm.value || companySearchTerm.value.length < 2) {
    return null;
  }
  return {
    organizationId: organizationId.value,
    name: companySearchTerm.value,
    limit: 10,
  };
});

const { data: companiesQueryData } = useCompanies(companiesSearchParams);

const { mutateAsync: registerContactMutation, isPending: isRegistering } = useRegisterContact();
const { mutateAsync: requestEnrichmentMutation, isPending: isEnrichmentLoading } =
  useRequestEnrichment();

const { showUsageBasedFeaturesModalOnce, isUsageBlocked } = useBillingStatus();

const bucketOptions = computed(() => bucketOptionsData.value || []);
const companiesData = computed(() => companiesQueryData.value?.data || []);

const taskTypeOptions = computed(() => {
  return Object.values(TaskType).map((task) => ({
    label: $filters?.capitalize(task) || task,
    value: task,
    icon: getTaskTypeIcon(task),
  }));
});

const bucketSelectOptions = computed(() => {
  return bucketOptions.value.map((bucket) => ({
    label: bucket.name,
    value: bucket.id,
    icon: "fa6-solid:bucket",
    iconColor: bucket.colorHex,
  }));
});

const phoneLabelOptions = [
  { label: "Mobile", value: "Mobile" },
  { label: "Direct", value: "Direct" },
];

const industryOptions = INDUSTRY_OPTIONS;

const capitalize = (str) => {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

const closeModal = () => {
  emit("closeAddContactModal");
};

const restoreAddContactDraft = () => {
  const draft = loadAddContactDraft();
  if (!draft) return;
  if (draft.fname) formData.value.fname = draft.fname;
  if (draft.lname) formData.value.lname = draft.lname;
  if (draft.email) formData.value.email = draft.email;
  // Don't clobber a deliberately-passed phone number (e.g. "add contact" from a call log entry)
  // with a stale draft saved under the same org key.
  if (draft.mobileNumber && !props.selectedPhoneNumber) {
    formData.value.mobileNumber = draft.mobileNumber;
  }
  if (draft.website) formData.value.website = draft.website;
  if (draft.industry) formData.value.industry = draft.industry;
  if (draft.jobTitle) formData.value.jobTitle = draft.jobTitle;
  if (draft.linkedInUrl) formData.value.linkedInUrl = draft.linkedInUrl;
  if (draft.taskType) formData.value.taskType = draft.taskType;
  if (draft.bucketOptionId) formData.value.bucketOptionId = draft.bucketOptionId;
  if (draft.followUpDate) formData.value.followUpDate = draft.followUpDate;
  if (draft.additionalEmails?.length) formData.value.additionalEmails = draft.additionalEmails;
  if (draft.additionalPhoneNumbers?.some((p) => p)) {
    formData.value.additionalPhoneNumbers = draft.additionalPhoneNumbers;
  }
  if (draft.labeledAdditionalPhoneNumbers?.some((p) => p?.number)) {
    formData.value.labeledAdditionalPhoneNumbers = draft.labeledAdditionalPhoneNumbers;
  }
  if (draft.company) formData.value.company = draft.company;
  if (draft.companySearchTerm) companySearchTerm.value = draft.companySearchTerm;
};

const addAdditionalEmail = () => {
  formData.value.additionalEmails.push("");
};

const removeAdditionalEmail = (index) => {
  if (formData.value.additionalEmails.length > 0) {
    formData.value.additionalEmails.splice(index, 1);
  }
};

const addAdditionalPhoneNumber = () => {
  formData.value.labeledAdditionalPhoneNumbers.push({
    number: null,
    label: "Mobile",
  });
};

const removeAdditionalPhoneNumber = (index) => {
  if (formData.value.labeledAdditionalPhoneNumbers.length > 1) {
    formData.value.labeledAdditionalPhoneNumbers.splice(index, 1);
  }
};

const addCustomField = () => {
  formData.value.customFields.push({ label: "", value: "" });
};

const removeCustomField = (index) => {
  formData.value.customFields.splice(index, 1);
};

const debouncedCompanySearch = debounce(() => {}, 400);

const handleCompanySearch = () => {
  debouncedCompanySearch();
};

const handleHideSearchDropdown = () => {
  setTimeout(() => {
    showSearchDropdown.value = false;
  }, 200);
};

const handleSelectCompany = (company) => {
  formData.value.industry = company.industry;
  formData.value.company = company.name;
  formData.value.website = company.website;
  companySearchTerm.value = company.name;
  showSearchDropdown.value = false;
};

const handleRequestEnrichment = async () => {
  await showUsageBasedFeaturesModalOnce("enrichment", async () => {
    const { linkedInUrl, fname, lname, company } = formData.value;
    if (!linkedInUrl && (!fname || !lname || !company)) {
      $toast?.info("Please enter required fields.");
      return;
    }

    try {
      const result = await requestEnrichmentMutation({
        linkedInUrl: linkedInUrl || "",
        fname: fname || "",
        lname: lname || "",
        jobTitle: formData.value.jobTitle || "",
        enrichEmail: formData.value.enrichEmail,
        enrichPhoneNumber: formData.value.enrichPhoneNumber,
        organizationId: organizationId.value,
      });

      if (result) {
        enrichmentResult.value = result;
      }
    } catch (error) {
      $toast?.error(error.message || "Failed to request enrichment");
    }
  });
};

const handleSubmit = () => {
  if (!contactFormRef.value) {
    $toast?.error("Form is not ready. Please try again.");
    return;
  }

  if (typeof contactFormRef.value.handleSubmit !== "function") {
    const formElement = contactFormRef.value.$el;
    if (formElement && formElement.requestSubmit) {
      formElement.requestSubmit();
    } else {
      $toast?.error("Unable to submit form. Please check all required fields.");
    }
    return;
  }

  const submitHandler = contactFormRef.value.handleSubmit(handleFormSubmit);
  if (typeof submitHandler === "function") {
    submitHandler();
  } else {
    $toast?.error("Form submission failed. Please try again.");
  }
};

const handleFormSubmit = async () => {
  try {
    const additionalPhoneNumbers = formData.value.additionalPhoneNumbers.filter(
      (phoneNumber) => phoneNumber
    );
    const additionalEmails = formData.value.additionalEmails.filter((email) => email);
    const customFields = (formData.value.customFields || []).filter(
      (field) =>
        field.fieldId && field.value !== null && field.value !== undefined && field.value !== ""
    );
    const primaryPhone =
      formData.value.labeledAdditionalPhoneNumbers[0]?.number || formData.value.mobileNumber || "";

    const payload = {
      fname: formData.value.fname,
      lname: formData.value.lname,
      email: formData.value.email,
      jobTitle: formData.value.jobTitle || "",
      linkedInUrl: formData.value.linkedInUrl || "",
      phoneNumber: primaryPhone,
      additionalEmails,
      additionalPhoneNumbers,
      company: formData.value.company || companySearchTerm.value || "",
      organizationId: organizationId.value,
      taskType: formData.value.taskType,
      bucketOptionId: formData.value.bucketOptionId,
      followUpDate: formData.value.followUpDate,
      enrichmentId: enrichmentResult.value?.id,
      ...(customFields.length ? { customFields } : {}),
    };

    await registerContactMutation(payload);

    clearAddContactDraft();
    emit("refreshContacts");
    closeModal();
  } catch (error) {
    $toast?.error(error.message || "Failed to create contact");
  }
};

onMounted(() => {
  if (props.selectedPhoneNumber) {
    formData.value.mobileNumber = props.selectedPhoneNumber;
  }
  formData.value.organizationId = organizationId.value;
  restoreAddContactDraft();
});
</script>

<style scoped>
:deep(.add-contact-modal .ui-modal__dialog--lg) {
  max-width: 720px;
}

.form {
  padding: 24px;
}

.linkedin-quick-add-section {
  position: relative;
  padding: 24px;
  padding-top: 16px;
  border: 2px dashed #d0d5dd;
  border-radius: 12px;
  background-color: #fafafa;
  margin-bottom: 32px;
}

.quick-add-button {
  position: absolute;
  top: -14px;
  left: 16px;
  width: auto;
  padding: 6px 14px;
  margin-bottom: 0;
  z-index: 10;
  pointer-events: none;
  background-color: #3e3efe !important;
  border-radius: 9999px;
  padding: 2px;
}

.quick-add-button :deep(.ui-button) {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.7rem;
  padding: 4px 12px;
  cursor: default;
  background-color: #3e3efe !important;
  border-color: #3e3efe !important;
  opacity: 1 !important;
}

.quick-add-button :deep(.ui-button--disabled) {
  opacity: 1 !important;
  background-color: #3e3efe !important;
  border-color: #3e3efe !important;
}

.quick-add-button :deep(.ui-button--disabled:hover) {
  background-color: #3e3efe !important;
  border-color: #3e3efe !important;
  opacity: 1 !important;
}

.quick-add-button .button-icon-left,
.quick-add-button .button-icon-right {
  font-size: 14px;
  width: 14px;
  height: 14px;
  color: #ffffff;
  flex-shrink: 0;
}

.linkedin-input-wrapper {
  display: flex;
  gap: 12px;
  margin-top: 20px;
  margin-bottom: 16px;
}

.linkedin-url-input {
  flex: 1;
}

.enrich-button {
  flex-shrink: 0;
}

.enrich-button:deep(.ui-button--disabled) {
  pointer-events: auto;
}

.enrich-options {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
}

.bucket-info-notice {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  background-color: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  margin-bottom: 16px;
  font-size: 0.8rem;
  color: #1e40af;
}

.bucket-info-notice .notice-icon {
  font-size: 16px;
  flex-shrink: 0;
  margin-top: 1px;
}

.enrichment-info-notice {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  background-color: #fef3c7;
  border: 1px solid #fcd34d;
  border-radius: 8px;
  margin-top: 12px;
  font-size: 0.8rem;
  color: #92400e;
}

.enrichment-info-notice .notice-icon {
  font-size: 16px;
  flex-shrink: 0;
  margin-top: 1px;
}

.enrich-checkbox-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.enrich-checkbox-wrapper :deep(.ui-checkbox) {
  margin: 0;
}

.enrich-checkbox-wrapper :deep(.ui-checkbox__label) {
  margin: 0;
  padding: 0;
}

.enrich-checkbox-wrapper .checkbox-icon {
  font-size: 18px;
  width: 18px;
  height: 18px;
  color: #6b7280;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  vertical-align: middle;
}

.checkbox-label-text {
  font-size: 0.875rem;
  font-weight: 500;
  color: #344054;
  user-select: none;
}

/* Section Divider */
.section-divider {
  position: relative;
  margin: 32px 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.divider-label {
  position: relative;
  background-color: #ffffff;
  padding: 0 16px;
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  z-index: 1;
}

.divider-line {
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 1px;
  background-color: #e4e4e7;
  z-index: 0;
}

.contact-details-form {
  margin-top: 24px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-bottom: 24px;
}

.form-column {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-group {
  margin-bottom: 24px;
}

.form-error {
  font-size: 0.75rem;
  color: #ef4444;
  margin-top: 4px;
}

.dynamic-section {
  margin-top: 8px;
}

.full-width-section {
  width: 100%;
  margin-top: 16px;
  margin-bottom: 0;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
}

.section-label {
  font-size: 0.82rem;
  font-weight: 400;
  color: #344054;
}

.add-field-btn-header {
  color: #3e3efe;
  font-weight: 500;
  font-size: 0.75rem;
  padding: 4px 8px;
}

.add-field-btn-header:hover {
  background-color: #f3f1ff;
}

.main-email-field {
  margin-bottom: 12px;
}

.phone-number-row {
  display: flex;
  flex-direction: column;
  gap: 0;
  margin-bottom: 12px;
  position: relative;
}

.phone-number-row:not(.additional-field) {
  margin-bottom: 12px;
}

.phone-number-row.additional-field {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px dashed #e5e7eb;
  position: relative;
}

.phone-fields-wrapper {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  width: 100%;
}

.email-row {
  display: flex;
  flex-direction: column;
  gap: 0;
  margin-bottom: 12px;
  position: relative;
}

.email-row.additional-field {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px dashed #e5e7eb;
  position: relative;
}

.dynamic-section .ui-input {
  margin-bottom: 0;
}

.phone-type-select {
  flex: 0 0 140px;
  min-width: 0;
}

.phone-input-wrapper {
  flex: 1;
  min-width: 0;
}

.add-field-btn {
  margin-top: 8px;
  color: #3e3efe;
  font-weight: 500;
}

.add-field-btn:hover {
  background-color: #f3f1ff;
}

.delete-field-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  margin-top: 28px;
  background: transparent;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  color: #6b7280;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.delete-field-btn:hover:not(:disabled) {
  background-color: #fee2e2;
  color: #dc2626;
}

.delete-field-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.delete-field-btn .iconify {
  font-size: 18px;
  width: 18px;
  height: 18px;
}

.additional-field-header {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-bottom: 6px;
}

.enrichment-sent-state {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 20px 24px;
  margin-bottom: 32px;
  background-color: #f0fdf4;
  border: 1px solid #86efac;
  border-radius: 12px;
}

.enrichment-sent-icon {
  font-size: 28px;
  width: 28px;
  height: 28px;
  color: #16a34a;
  flex-shrink: 0;
}

.enrichment-sent-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.enrichment-sent-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #15803d;
}

.enrichment-sent-sub {
  font-size: 0.8rem;
  color: #166534;
  line-height: 1.4;
}

.remove-additional-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  background: transparent;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  cursor: pointer;
  color: #6b7280;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.remove-additional-btn:hover:not(:disabled) {
  background-color: #fee2e2;
  border-color: #fca5a5;
  color: #dc2626;
}

.remove-additional-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.remove-additional-btn .iconify {
  font-size: 14px;
  width: 14px;
  height: 14px;
}

.custom-fields-section {
  margin-top: 24px;
  padding-top: 0;
  border-top: none;
}

.custom-field-row {
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  gap: 12px;
  align-items: flex-start;
  margin-bottom: 16px;
}

.custom-field-input {
  flex: 1;
}

.global-search-container {
  position: relative;
}

.global-search-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 4px;
  background: #ffffff;
  border: 1px solid #e4e4e7;
  border-radius: 8px;
  box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.12);
  z-index: 2000;
  max-height: 250px;
  overflow-y: auto;
}

.global-search-item {
  padding: 10px 16px;
  cursor: pointer;
  font-size: 0.875rem;
  color: #18181b;
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.2s ease;
}

.global-search-item:last-child {
  border-bottom: none;
}

.global-search-item:hover {
  background: #f1f7fd;
}

.global-search-item .company-name {
  margin: 0;
  font-size: 0.875rem;
  font-weight: 500;
  color: #3e3efe;
}

@media (max-width: 1024px) {
  .form-row {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .custom-field-row {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .phone-number-row {
    flex-direction: column;
  }

  .phone-type-select {
    flex: 1;
    width: 100%;
  }
}

@media (max-width: 640px) {
  .linkedin-quick-add-section {
    padding: 16px;
  }

  .linkedin-input-wrapper {
    flex-direction: column;
  }

  .enrich-options {
    flex-direction: column;
    gap: 12px;
  }

  .form-row {
    gap: 16px;
  }

  .form-column {
    gap: 16px;
  }
}
</style>
